import { useEffect, useMemo } from 'react';

import { BigNumber } from 'bignumber.js';
import {
  isDirectExecutionMethod,
  useFormContext,
  useField,
} from 'modules/operation/common/forms/operationForm';

export const useParametorsSection = () => {
  const { values } = useFormContext();
  const { helpers: volumeHelpers } = useField('volume');
  const { helpers: counterpartyNameHelpers, field: counterpartyNameField } =
    useField('counterpartyName');

  const currency = values.order?.record?.currency || '';

  const nominalQuantity = useMemo(
    () =>
      values.order?.record?.nominalValue && values.quantity
        ? BigNumber(values.order?.record?.nominalValue)
            .multipliedBy(values.quantity)
            .toFixed()
        : '',
    [values.order?.record?.nominalValue, values.quantity]
  );

  const isCounterpartyNameRequired = isDirectExecutionMethod(
    values.executionMethod
  );

  const isVolumeDisabled = useMemo(
    () => values.netUnitPrice === undefined || values.quantity === undefined,
    [values.netUnitPrice, values.quantity]
  );

  // Clear counterparty name value and error when it becomes not required
  useEffect(() => {
    if (!isCounterpartyNameRequired && counterpartyNameField.value) {
      counterpartyNameHelpers.setValue('');
    }
    counterpartyNameHelpers.setError(undefined);
  }, [
    isCounterpartyNameRequired,
    counterpartyNameHelpers,
    counterpartyNameField.value,
  ]);

  // Calculate transaction amount when net unit price, quantity or accruedCouponIncome changes
  useEffect(() => {
    if (values.netUnitPrice !== undefined && values.quantity !== undefined) {
      const netUnitPriceBigNumber = BigNumber(values.netUnitPrice);
      const quantityBigNumber = BigNumber(values.quantity);
      if (netUnitPriceBigNumber.isNaN() || quantityBigNumber.isNaN()) {
        volumeHelpers.setValue(undefined);
        volumeHelpers.setError(undefined);
        return;
      }
      volumeHelpers.setValue(
        netUnitPriceBigNumber
          .multipliedBy(quantityBigNumber)
          .plus(
            values.accruedCouponIncome
              ? BigNumber(values.accruedCouponIncome)
              : 0
          )
          .toFixed()
      );
      volumeHelpers.setError(undefined);
    } else {
      volumeHelpers.setValue(undefined);
    }
  }, [
    volumeHelpers,
    values.netUnitPrice,
    values.quantity,
    values.accruedCouponIncome,
  ]);

  return {
    currency,
    nominalQuantity,
    isCounterpartyNameRequired,
    isVolumeDisabled,
  };
};
